import { Fragment } from 'react';
import {
    Dialog,
    DialogPanel,
    Transition,
    TransitionChild,
} from '@headlessui/react';
import { setPreventUnload } from 'flow/actions/app-actions';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import useAppContext from 'flow/hooks/useAppContext';

interface Props {
    imageSrc: string;
    fileName: string;
    onClose: () => void;
}

/**
 * Shown as a normal nested child of ChatPostDisplay (itself inside
 * ChatPostModal's DialogPanel) rather than through the ModalProvider stack,
 * which only ever renders the topmost modal — that would unmount
 * ChatPostModal and lose the in-progress comment draft. A real nested
 * Dialog lets HeadlessUI's own top-layer stacking scope Escape/outside-click
 * to this one while it's open, instead of also closing the post modal.
 */
const PostImageLightbox = ({ imageSrc, fileName, onClose }: Props) => {
    const { i18next } = useAppContext();

    return (
        <Transition show={true} as={Fragment}>
            <Dialog
                static={true}
                as="div"
                className="relative z-30"
                onClose={onClose}
            >
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <DialogPanel className="no-dark fixed inset-0 h-screen w-screen">
                        <div className="flex h-full w-full flex-col bg-black/70 backdrop-blur-sm">
                            <div className="flex h-15 w-full items-center justify-end gap-3 bg-gray-800/70 px-3.75">
                                <a
                                    href={imageSrc}
                                    download={true}
                                    onClick={() => setPreventUnload(true)}
                                >
                                    <Icon
                                        iconKey="download"
                                        className="size-4.5 text-white"
                                    />
                                    <span className="sr-only">
                                        {i18next.t('Tooltip.Audio.Download')}
                                    </span>
                                </a>
                                <IconButton
                                    onClick={onClose}
                                    className="size-6"
                                    tooltipLabel={i18next.t(
                                        'Tooltip.Dialog.Close',
                                    )}
                                >
                                    <Icon
                                        iconKey="close"
                                        className="size-4.5 text-white"
                                    />
                                </IconButton>
                            </div>
                            <div
                                className="relative flex grow items-center justify-center"
                                onClick={(e) => {
                                    if (e.target === e.currentTarget) {
                                        onClose();
                                    }
                                }}
                            >
                                <img
                                    className="max-h-full max-w-full object-contain"
                                    src={imageSrc}
                                    alt={fileName}
                                />
                            </div>
                        </div>
                    </DialogPanel>
                </TransitionChild>
            </Dialog>
        </Transition>
    );
};

export default PostImageLightbox;
